import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Spin, Table } from 'antd';
import pluralize from 'pluralize';
import cx from 'classnames';
import moment from 'moment';
import { isNil } from 'lodash';

import { useAppDispatch } from 'hooks/useAppDispatch';
import { getBOAdminsPagedList } from 'redux/reducers/adminSlice';
import { pageSmallSizeTable } from 'constants/index';
import adminApiClient from 'api/adminApiClient';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import BaseAlert from 'components/wrappers/BaseAlert';
import {
  BOAdminViewModelPagedResponseModel,
  BOAdminViewModel,
} from 'api/adminApiClient';
import { getFullName } from 'utils/personUtils';
import BOAdminModal from 'components/modals/BOAdminModal';
import { ApiErrorMessage } from 'constants/ErrorMessages';

import styles from './AdminsPage.module.less';

export default function AdminsPage() {
  const [isAddBOAdminModalOpen, setIsAddBOAdminModalOpen] = useState(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [adminsData, setAdminsData] =
    useState<BOAdminViewModelPagedResponseModel>();
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const [isAdminsDataLoading, setIsAdminsDataLoading] = useState(false);

  const dispatch = useAppDispatch();

  const handleOpenBOAdminModal = () => {
    setIsAddBOAdminModalOpen(true);
  };

  const handleCloseBOAdminModal = () => {
    setIsAddBOAdminModalOpen(false);
  };

  const adminsCountMessage = pluralize(
    'Admin',
    adminsData?.totalCount || 0,
    true,
  );

  const loadAdminsDetails = useCallback(async () => {
    setIsAdminsDataLoading(true);

    const result = await dispatch(
      getBOAdminsPagedList({
        pageNumber: currentTablePage - 1,
        pageSize: pageSmallSizeTable,
      }),
    );

    if (getBOAdminsPagedList.fulfilled.match(result)) {
      setAdminsData(result.payload);
    } else {
      setAdminsData({
        totalCount: 0,
        details: [],
      });
    }

    setIsAdminsDataLoading(false);
    // eslint-disable-next-line
  }, [dispatch, currentTablePage]);

  const loadInitialData = useCallback(async () => {
    await loadAdminsDetails();

    setIsInitialDataLoaded(true);
  }, [loadAdminsDetails]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleDeactivate = useCallback(
    async (admin: BOAdminViewModel) => {
      const adminId = admin.adminId;
      if (!adminId) return;

      try {
        await adminApiClient.deactivateUser(adminId);
        BaseAlert.success('Admin has been deactivated successfully.');
        await loadAdminsDetails();
      } catch (error: unknown) {
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message: unknown }).message)
            : ApiErrorMessage;
        BaseAlert.error(message);
      }
    },
    [loadAdminsDetails],
  );

  const showDeactivateConfirm = useCallback(
    (admin: BOAdminViewModel) => {
      Modal.confirm({
        title:
          'If you deactivate this admin, they will not be able to sign in to the Back Office. Do you want to proceed?',
        icon: <span className="icon icon-attention-filled" />,
        async onOk() {
          await handleDeactivate(admin);
        },
      });
    },
    [handleDeactivate],
  );

  const columns = [
    {
      title: 'Admin Name',
      key: 'adminName',
      render: ({ firstName, lastName }: BOAdminViewModel) => (
        <span className="wrapped-text">{getFullName(firstName, lastName)}</span>
      ),
    },
    {
      title: 'Admin Email',
      key: 'adminEmail',
      render: ({ email }: BOAdminViewModel) => (
        <span className="wrapped-text">{email}</span>
      ),
    },
    {
      title: 'Last Login',
      key: 'lastLogin',
      render: ({ lastLogin }: BOAdminViewModel) =>
        !isNil(lastLogin) && (
          <span className="wrapped-text">
            {moment.utc(lastLogin).format('MM/DD/YYYY')}
          </span>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: ({ isDeleted }: BOAdminViewModel) => (
        <span className="wrapped-text">
          {isDeleted ? 'Deactivated' : 'Active'}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (admin: BOAdminViewModel) => (
        <Button
          onClick={() => showDeactivateConfirm(admin)}
          disabled={!!admin.isDeleted}
        >
          Deactivate
        </Button>
      ),
    },
  ];

  return (
    <>
      {isAddBOAdminModalOpen && (
        <BOAdminModal
          onClose={handleCloseBOAdminModal}
          onSuccess={loadAdminsDetails}
        />
      )}
      <div className="page-wrapper scroll-box">
        <div className="page-box page-holder page-inner">
          <div className="page-inner-container">
            {!isInitialDataLoaded && <Spin />}
            {isInitialDataLoaded && (
              <>
                <BaseTableHeader
                  title="BO Admins"
                  enableSubTitle={isInitialDataLoaded}
                  subTitle={adminsCountMessage}
                >
                  <Button
                    onClick={handleOpenBOAdminModal}
                    type="primary"
                    className="mobile-default"
                  >
                    Add BO Admin
                  </Button>
                </BaseTableHeader>

                <Table
                  showSorterTooltip={false}
                  className={cx(styles.table, 'custom-table')}
                  pagination={{
                    total: adminsData?.totalCount,
                    pageSize: pageSmallSizeTable,
                    onChange: setCurrentTablePage,
                    current: currentTablePage,
                    showSizeChanger: false,
                  }}
                  columns={columns}
                  dataSource={adminsData?.details}
                  rowKey={(record) => record?.email as string}
                  loading={isAdminsDataLoading}
                  locale={{
                    emptyText: 'You currently do not have any admin users.',
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
